import { useCallback, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import JTHelmet from '../JTHelmet';
import NoChildSelected from '@/components/Parent/NoChildSelected';
import SwitchChildConfirm from '@/components/Parent/SwitchChildConfirm';
import { getDiscounts, getOutlet, getPaymentTypes, getServingTimes } from '@/store/slices/contentSlice';
import { getCustomer } from '@/store/slices/authSlice';
import usePageTracking from '../../hooks/usePageTracking';
import { getDiscountCustomer } from '@/store/slices/cartSlice';
export default function Layout() {
	usePageTracking();

	const dispatch = useDispatch();
	const location = useLocation();
	const observedElementRef = useRef(null);
	const lastFetchedCustomerId = useRef(null);

	const client = useSelector((state) => state.auth.user?.client?.id);
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const selectedCustomerId = useSelector((state) => state.auth.selectedCustomerId);
	const accountType = useSelector((state) => state.auth.accountType);
	const childrenLoaded = useSelector((state) => state.auth.childrenLoaded);
	const errRequest = useSelector((state) => state.content.errRequest);

	const outletId = selectedOutlet?.id;
	const waitingForChildren = accountType === 'parent' && !childrenLoaded;
	// Family dashboard (/) does not require a selected child; menu/cart/orders do.
	const isFamilyDashboard =
		location.pathname === '/' ||
		location.pathname === '' ||
		location.pathname === '/dashboard';
	const needsChildContext =
		accountType === 'parent' &&
		childrenLoaded &&
		!selectedCustomerId &&
		!isFamilyDashboard;

	const onResize = useCallback(() => {
		if (observedElementRef.current) dispatch({ type: 'content/setInner', payload: window.innerHeight });
	}, [dispatch]);

	useEffect(() => {
		window.addEventListener('resize', onResize);
		onResize();
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	// Do NOT log out when outlet is missing — zero-child / pre-selection parents stay signed in.
	useEffect(() => {
		if (outletId) {
			dispatch(getOutlet(outletId));
		}
	}, [outletId, dispatch]);

	useEffect(() => {
		if (outletId) {
			dispatch(getPaymentTypes({ outlet: outletId, max_size: true, ordering: 'id' }));
		}
	}, [outletId, client, dispatch]);

	useEffect(() => {
		if (outletId) {
			dispatch(getServingTimes({ outlet: outletId, max_size: true }));
		}
	}, [outletId, dispatch]);

	useEffect(() => {
		if (outletId) {
			dispatch(getDiscounts({ outlet: outletId, max_size: true }));
		}
	}, [outletId, client, dispatch]);

	useEffect(() => {
		if (!selectedCustomerId) {
			lastFetchedCustomerId.current = null;
			return;
		}
		if (lastFetchedCustomerId.current === selectedCustomerId) return;
		lastFetchedCustomerId.current = selectedCustomerId;
		dispatch(getCustomer({ params: {}, id: selectedCustomerId }));
	}, [selectedCustomerId, dispatch]);

	useEffect(() => {
		if (selectedCustomerId) {
			dispatch(getDiscountCustomer(selectedCustomerId));
		}
	}, [selectedCustomerId, dispatch]);

	useEffect(() => {
		if (errRequest && selectedOutlet) {
			// reserved for outlet error handling
		}
	}, [errRequest, selectedOutlet, dispatch]);

	return (
		<>
			<JTHelmet />
			<div ref={observedElementRef} className='layout'>
				{waitingForChildren ? (
					<div className='flex items-center justify-center min-h-[40vh]'>
						<CircularProgress size={28} sx={{ color: 'var(--bzGreen)' }} />
					</div>
				) : needsChildContext ? (
					<NoChildSelected />
				) : (
					<Outlet />
				)}
			</div>
			<SwitchChildConfirm />
		</>
	);
}
